<template>
  <RevAccordionItem>
    <template #prefix><IconQuality /></template>
    <template #title>
      <span v-if="isInWarranty" class="text-static-success-hi">
        {{ i18n(translations.inWarrantyTitle) }}
      </span>
      <span v-else class="text-static-danger-hi">
        {{ i18n(translations.outWarrantyTitle) }}
      </span>
    </template>
    <template #description>
      <span data-allow-mismatch="text">
        <template v-if="!isInWarranty">
          {{ i18n(translations.outOfWarrantyDescription) }}
        </template>
        <template v-if="isInSellerWarranty">
          {{ sellerWarrantyDescription }}
        </template>
        <template v-if="isInSellerWarrantyExtension">
          {{ sellerWarrantyExtensionDescription }}
        </template>
        <template v-if="isInBackmarketWarranty">
          {{ backmarketWarrantyDescription }}
        </template>
      </span>
    </template>
    <template #body>
      <ul class="space-y-16">
        <li v-if="hasSellerWarranty">
          <p class="font-weight-body-2-bold">
            {{ i18n(translations.sellerWarrantyTitle) }}
          </p>
          <p>{{ sellerWarrantyDescription }}</p>
        </li>
        <li v-if="hasSellerWarrantyExtension">
          <p class="font-weight-body-2-bold">
            {{ i18n(translations.sellerWarrantyExtensionTitle) }}
          </p>
          <p>{{ sellerWarrantyExtensionDescription }}</p>
        </li>
        <li v-if="hasBackmarketWarranty">
          <p class="font-weight-body-2-bold">
            {{ i18n(translations.backmarketWarrantyTitle) }}
          </p>
          <p>{{ backmarketWarrantyDescription }}</p>
        </li>
      </ul>
    </template>
  </RevAccordionItem>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { isFutureDate } from '@backmarket/utils/date/isFutureDate'
import { RevAccordionItem } from '@ds/components/AccordionItem'
import { IconQuality } from '@ds/icons/IconQuality'

import type { Coverage } from '~/api/care-folders/types/care-folder'

import translations from './Warranty.translations'

const i18n = useI18n()

const props = defineProps<{
  coverage: Coverage
}>()

const hasSellerWarranty = computed(() => props.coverage.merchantWarranty)
const hasSellerWarrantyExtension = computed(
  () => props.coverage.merchantWarrantyExtension,
)
const hasBackmarketWarranty = computed(() => props.coverage.backmarketWarranty)

const isInSellerWarranty = computed(
  () =>
    hasSellerWarranty.value &&
    isFutureDate(props.coverage.merchantWarranty.endDate),
)
const isInSellerWarrantyExtension = computed(
  () =>
    hasSellerWarrantyExtension.value &&
    isFutureDate(props.coverage.merchantWarrantyExtension.endDate),
)
const isInBackmarketWarranty = computed(
  () =>
    hasBackmarketWarranty.value &&
    isFutureDate(props.coverage.backmarketWarranty.endDate),
)
const isInWarranty = computed(
  () =>
    isInSellerWarranty.value ||
    isInSellerWarrantyExtension.value ||
    isInBackmarketWarranty.value,
)

const sellerWarrantyDescription = computed(() =>
  i18n(translations.sellerWarrantyDescription, {
    duration: props.coverage.merchantWarranty.duration,
    date: new Date(props.coverage.merchantWarranty.endDate),
  }),
)
const sellerWarrantyExtensionDescription = computed(() =>
  i18n(translations.sellerWarrantyExtensionDescription, {
    duration: props.coverage.merchantWarrantyExtension.duration,
    date: new Date(props.coverage.merchantWarrantyExtension.endDate),
  }),
)
const backmarketWarrantyDescription = computed(() =>
  i18n(translations.backmarketWarrantyDescription, {
    duration: props.coverage.backmarketWarranty.duration,
    date: new Date(props.coverage.backmarketWarranty.endDate),
  }),
)
</script>
